import { useState, useEffect } from 'react';
import { HardDrive, Unplug, CheckCircle, AlertTriangle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

export function GoogleDriveAuth() {
    const [status, setStatus] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [configMode, setConfigMode] = useState(false);
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchStatus();

        // Check for URL params if we just redirected back
        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'drive_connected') {
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            fetchStatus();
        } else if (params.get('error')) {
            setError(params.get('error') || 'Unknown error during authentication');
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const fetchStatus = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/auth/google/status`);
            const data = await res.json();
            setStatus(data);
        } catch (e) {
            console.error('Failed to fetch Google Auth status', e);
        }
        setLoading(false);
    };

    const handleConnect = () => {
        // Redirect to our backend auth endpoint
        const redirectUrl = encodeURIComponent(window.location.href);
        window.location.href = `${API_URL}/auth/google?redirect=${redirectUrl}`;
    };

    const handleDisconnect = async () => {
        if (!confirm('Are you sure you want to disconnect Google Drive? File uploads will stop working until reconnected.')) return;
        
        setLoading(true);
        try {
            await fetch(`${API_URL}/auth/google/disconnect`, { method: 'POST' });
            await fetchStatus();
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const handleSaveConfig = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/auth/google/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ client_id: clientId, client_secret: clientSecret })
            });
            if (res.ok) {
                setConfigMode(false);
                setClientId('');
                setClientSecret('');
                fetchStatus();
            } else {
                const data = await res.json();
                setError(data.error || 'Failed to save config');
            }
        } catch (e) {
            setError('Failed to reach server');
        }
        setLoading(false);
    };

    if (loading) {
        return (
            <div className="p-6 bg-white rounded-xl border border-slate-200 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-1/4 mb-4"></div>
                <div className="h-10 bg-slate-200 rounded w-full"></div>
            </div>
        );
    }

    const formatBytes = (bytes: string | number) => {
        const b = typeof bytes === 'string' ? parseInt(bytes) : bytes;
        if (isNaN(b) || b === 0) return '0 GB';
        return (b / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mt-6">
            <div className="border-b border-slate-200 p-6 flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                        <HardDrive className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-900">Google Drive Integration</h3>
                        <p className="text-sm text-slate-500">Connect your Google account to store media files securely.</p>
                    </div>
                </div>
                {status?.connected ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Connected
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Disconnected
                    </span>
                )}
            </div>

            <div className="p-6">
                {error && (
                    <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                        <div>
                            <p className="font-medium">Authentication Error</p>
                            <p className="text-sm mt-1">{error}</p>
                        </div>
                    </div>
                )}

                {status?.connected ? (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100">
                            <div className="flex items-center gap-4">
                                {status.picture ? (
                                    <img src={status.picture} alt="Profile" className="w-12 h-12 rounded-full border-2 border-white shadow-sm" />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                                        {status.email?.charAt(0).toUpperCase() || 'U'}
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-medium text-slate-900">{status.email || 'Unknown User'}</p>
                                    <p className="text-xs text-slate-500 mt-1">Google Drive Account</p>
                                </div>
                            </div>
                            <button 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200 px-4 py-2 rounded-md flex items-center transition-colors text-sm font-medium"
                                onClick={handleDisconnect}
                            >
                                <Unplug className="w-4 h-4 mr-2" />
                                Disconnect
                            </button>
                        </div>

                        {status.quota && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-slate-700">Storage Usage</span>
                                    <span className="text-sm text-slate-500">
                                        {formatBytes(status.quota.usage)} / {status.quota.limit ? formatBytes(status.quota.limit) : 'Unlimited'}
                                    </span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                                    <div 
                                        className="bg-blue-600 h-2.5 rounded-full transition-all"
                                        style={{ 
                                            width: status.quota.limit 
                                                ? `${Math.min(100, (parseInt(status.quota.usage) / parseInt(status.quota.limit)) * 100)}%` 
                                                : '0%' 
                                        }}
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : configMode ? (
                    <div className="space-y-4">
                        <div className="p-4 bg-blue-50 text-blue-800 rounded-lg text-sm mb-4">
                            To use Google Drive OAuth, you must provide your Client ID and Secret from the Google Cloud Console.
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">OAuth Client ID</label>
                            <input 
                                type="text"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={clientId}
                                onChange={(e) => setClientId(e.target.value)}
                                placeholder="123456789-xxxx.apps.googleusercontent.com"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">OAuth Client Secret</label>
                            <input 
                                type="password"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={clientSecret}
                                onChange={(e) => setClientSecret(e.target.value)}
                                placeholder="GOCSPX-xxxx"
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button 
                                onClick={handleSaveConfig} 
                                disabled={!clientId || !clientSecret}
                                className="px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-50 text-sm font-medium transition-colors"
                            >
                                Save Configuration
                            </button>
                            <button 
                                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 text-sm font-medium transition-colors"
                                onClick={() => setConfigMode(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12.0003 12C14.2094 12 16.0003 10.2091 16.0003 8C16.0003 5.79086 14.2094 4 12.0003 4C9.79116 4 8.0003 5.79086 8.0003 8C8.0003 10.2091 9.79116 12 12.0003 12Z" />
                                <path d="M12.0003 14C7.58197 14 4.0003 17.5817 4.0003 22H20.0003C20.0003 17.5817 16.4186 14 12.0003 14Z" />
                            </svg>
                        </div>
                        <h4 className="text-lg font-medium text-slate-900 mb-2">Connect Google Drive</h4>
                        <p className="text-slate-500 max-w-md mx-auto mb-6 text-sm">
                            Connect your Google account to automatically store and serve media files for your stories. We use OAuth 2.0 for secure, offline access.
                        </p>
                        <div className="flex items-center justify-center gap-4">
                            <button 
                                onClick={handleConnect} 
                                className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm flex items-center px-4 py-2 rounded-md font-medium text-sm transition-colors"
                            >
                                <svg className="w-4 h-4 mr-2 bg-white rounded-full p-0.5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Sign in with Google
                            </button>
                            <button 
                                onClick={() => setConfigMode(true)}
                                className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
                            >
                                Configure Credentials
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
